/**
 * PingDownload Class is used for converting PingDownload OperationType into a sequence of operations that are going to
 * ping server until file is ready to download.
 * Pinging and download are happening in automatically created AsyncDispatcher in order to prevent blocking of main
 * dispatcher. After download is completed, AsyncDispatcher will be destroyed automatically.
 * While file is preparing to download, download widget will be active
 * and it will display text 'Preparing download'. When server returns that file is ready for download, download widget will fade away in 3s.
 *
 * @author Branislav Bogdanovic
 * @contributors Zarko Radojevic
 *
 * Example:
 {
    ping: {
      template: {
        method: "get",
        params: {
          bind: {
            data: "{data}"
          }
        },
        bind: {
          url: '"/submissions/" + ({submissionId} ? {submissionId} : {selectedSubmissions[0].submissionId}) + "/download"'
        }
      }
    },
    pingBreakCondition: ["messages"],
    pingCondition: "processingFinished",
    delay: 1000,
    pingErrorOperations: [{
      moduleName: "AbstractSection",
      name: "addDueDate",
      type: OperationType.addOperationData
    }],
    mappingName: "pingResponseDownloadData",
    download: {
      template: {
        method: "get",
        params: {
          bind: {
            data: "{data}"
          }
        },
        bind: {
          url: '"/submissions/download/" + {pingResponseDownloadData.downloadId}',
        }
      }
    },
    beforeDownloadOperations: [{
      moduleName: "AbstractSection",
      type: OperationType.maskModule
    }],
    withIndicator: true,
    pingOperations: [{
      moduleName: "ActiveSessions",
      name: "getColumnConfiguration",
      type: OperationType.deleteAllOperationData
    }],
    type: OperationType.pingDownload
  }
 *
 */
import * as _ from 'lodash';
import uuid from 'uuid-random';
import {
  ASYNC_DOWNLOAD_DISPATCHER_PARTIAL_MATCH_SPLITTER,
  ASYNC_DOWNLOAD_DISPATCHER_PREFIX,
  DOWNLOAD_INDICATOR_WIDGET
} from '../config/Constants';
import { DispatcherManager } from '../Dispatcher';
import OperationError from './OperationError';
import {
  Action,
  AddPersistentDataOperation,
  LoopObjectType,
  Operation,
  OperationType,
  PingDownloadOperation,
  Position,
  RemovePersistentDataOperation,
  RequestData,
  RequestOperation,
  StackOperation,
  Stop,
  StopAllOperation,
  UnlinkOperation,
  WarningType
} from '../Types';
import ComplexOperationToAction from './ComplexOperationToAction';

class PingDownload extends ComplexOperationToAction {
  /**
   * Operations which should be executed after file(s) is/are downloaded.
   */
  private _afterDownloadOperations: Action;
  /**
   * Config object for ping request (ping request is repeating on each delay and checking if analyze for download is finished).
   */
  private _ping: RequestData;
  /**
   * Delay number for ping request.
   */
  private _delay: number;
  /**
   * This property gets value from ping response which is used for stopping/continuing ping request. Expected value
   * is string property from server response.
   */
  private _pingCondition: string;
  /**
   * Represents array of server returns message(s).
   * If ping request failure then server returns message(s) which should be shown in warning dialog. Expected value
   * is array of strings properties from server response.
   */
  private _pingBreakCondition: string[];
  /**
   * Operations which are executed after ping request breaks down.
   * These operations are executed before warning dialog with messages (messages which server sends on ping request failure) is shown.
   */
  private _pingErrorOperations: Action;
  /**
   * If ping request analyze is finished successfully, then file(folder) can be downloaded.
   * This download url request can't be seen in network ajax requests because it is placed in form.
   * By default download configuration is same as for ping object. If download will go trough another end point, then
   * configure download.
   */
  private _download: RequestData;
  /**
   * Operations which should be executed before file(s) is/are downloaded.
   */
  private _beforeDownloadOperations: Action;
  /**
   * If `true` then widget with message that downloading is in progress will be shown, false otherwise.
   */
  private _withIndicator: boolean = false;
  /**
   * Represents a name under the ping response data(from each ping request) will be saved. After each request, the value of mapping name is overridden.
   */
  private _mappingName: string = 'pingDownloadData';
  /**
   * Operations which are executed after each ping request, until ping request is finished or error happens.
   */
  private _pingOperations: Action;
  /**
   * Dispatcher id which will be created and in which whole download ping operation will be executed.
   * @internalProperty
   */
  private _callingDispatcherId: string;
  /**
   * Newly created dispatcher id.
   * @internalProperty
   */
  private _asyncDispatcherId: string;
  /**
   * Used when operation type is `OperationType.pingDownload` to save which dispatcher called first request and
   * triggered creation of generic Async dispatchers.
   * @internalProperty
   */
  private static _pingInvocation: { [key: string]: string } = {};

  /**
   * Sets initialize data.
   * @param o PingDownload operation
   * @param params Params data from appOperationData
   * @param appDispatcherId Current dispatcher id
   */
  constructor(o: PingDownloadOperation, params: any, appDispatcherId: string) {
    super(o, appDispatcherId);
    const uniqueId = uuid();
    const {
      afterDownloadOperations = [],
      ping = {},
      delay = 3000,
      pingCondition,
      pingBreakCondition = [],
      pingErrorOperations = [],
      download = {},
      beforeDownloadOperations = [],
      withIndicator = false,
      mappingName = 'pingDownloadData',
      pingOperations = [],
      handleErrorStatuses
    } = o;
    this.afterDownloadOperations = afterDownloadOperations;
    this.delay = delay;
    this.pingCondition = pingCondition;
    this.pingBreakCondition = pingBreakCondition;
    this.pingErrorOperations = pingErrorOperations;
    this.beforeDownloadOperations = beforeDownloadOperations;
    this.withIndicator = withIndicator;
    this.mappingName = mappingName;
    this.pingOperations = pingOperations;
    this.handleErrorStatuses = handleErrorStatuses;

    this.asyncDispatcherId = `${ASYNC_DOWNLOAD_DISPATCHER_PREFIX}${ASYNC_DOWNLOAD_DISPATCHER_PARTIAL_MATCH_SPLITTER}${uniqueId}`;
    if (!PingDownload.pingInvocation[this.appDispatcherId] && !PingDownload.pingInvocation[this.asyncDispatcherId]) {
      PingDownload.pingInvocation[this.asyncDispatcherId] = this.appDispatcherId;
    }
    this.callingDispatcherId = PingDownload.pingInvocation[this.appDispatcherId] || this.appDispatcherId;

    // <debug>
    if (!pingBreakCondition.length) {
      throw new OperationError('Operation property "pingBreakCondition" can not be empty array! Please pass map to server response.', o);
    }

    this.addOriginalDataToEditor({
      ping: _.cloneDeep(ping),
      download: _.cloneDeep(download)
    });

    // </debug>

    _.set(ping, 'params', params);
    this.ping = ping;
    _.set(download, 'isDownload', true);
    this.download = download;
  }

  /**
   * Gets operations based on PingDownload operation.
   */
  public getOperations = (): Action => {
    const { actionName, mappingName, pingCondition } = this;
    const editorData: object = this.getEditorAdditionalData();
    const showInModules = [{
      actionName,
      data: {
        template: {
          bind: {
            [pingCondition]: `{${mappingName}.${pingCondition}}`,
            activeDownloadCount: this.appDispatcherId === this.callingDispatcherId ? '{numberOfActiveDownloads()} +' +
              ' 1' : '{numberOfActiveDownloads()}',
            downloadId: `{${mappingName}.downloadId}`
          }
        }
      },
      ...editorData,
      type: OperationType.data
    }, ...(this.withIndicator ? [{
      args: ['downloadId'],
      moduleId: DOWNLOAD_INDICATOR_WIDGET,
      moduleName: 'DownloadWidget',
      name: 'widgetFadeIn',
      ...editorData
    }] : []), ...this.pingOperations.map((op: any) => {
      op = this.prependAdditionalArgsToPingOperations(op, pingCondition);
      // <debug> - used only in combination with dispatcher editor
      op = DispatcherManager.addComplexOperationParentId(op, this.editorData.complexOperationId);
      // </debug>
      return op;
    })];

    const addPersistentResponseStatus: AddPersistentDataOperation = {
      persistent: ['responseStatus'],
      type: OperationType.addPersistentData
    };

    const removePersistentResponseStatus: RemovePersistentDataOperation = {
      persistent: ['responseStatus'],
      type: OperationType.removePersistentData
    };

    const actionTypeDispatcherId: string = this.appDispatcherId === this.callingDispatcherId ? this.asyncDispatcherId : this.appDispatcherId;
    const conditionOperations: Action = [{
      actionName,
      data: {
        template: {
          bind: {
            responseStatus: `{${mappingName}.status}`,
            [mappingName]: `{${mappingName}.data}`
          }
        }
      },
      ...editorData,
      type: OperationType.data
    }, ...showInModules, this.getPingerOperation(actionTypeDispatcherId)];

    const bindParams = {
      bind: {
        downloadId: `{${mappingName} && {${mappingName}.downloadId}`
      }
    };

    const showIndicatorWidget: Action = this.withIndicator ? [{
      data: {
        template: {
          bind: {
            downloadId: `{${mappingName}} && {${mappingName}.downloadId}`
          }
        }
      },
      actionName,
      name: 'DownloadIndicatorWidget',
      ...editorData,
      type: OperationType.action
    }] : [];

    return [{
      ...addPersistentResponseStatus
    }, {
      actionName,
      condition: '!{params}',
      conditionOperations: [{
        actionName,
        data: {
          template: {
            params: bindParams
          }
        },
        ...editorData,
        type: OperationType.data
      }],
      ...editorData,
      type: OperationType.conditional
    }, this.getPingRequest(), ...showIndicatorWidget, {
      actionName,
      // <debug>
      debug: this.debug,
      // </debug>
      condition: `!_.isString(_.get({${mappingName}}, "data")) && !(` + this.pingBreakCondition.map((conditionPart: string) => `_.get({${mappingName}}, "data.${conditionPart}")`).join('||') + ')',
      conditionOperations,
      elseOperations: [{
        actionName,
        data: {
          template: {
            bind: {
              responseStatus: `{${mappingName}.status}`,
              [mappingName]: `{${mappingName}.data}`
            }
          }
        },
        ...editorData,
        type: OperationType.data
      }, ...this.pingErrorOperations, this.getStopAllStacksInCurrentDispatcher(), ...this.getUpdateActiveDownloadCount(), this.getUnlinkCurrentDispatcher(), {
        actionName,
        condition: this.getPingBreakConditionErrorValue(),
        conditionOperations: [this.getCloseWidgetOperation()],
        type: OperationType.conditional
      }, ...this.getPingErrorWarning(), ...this.getComplexOperationEnd()],
      ...editorData,
      type: OperationType.conditional
    }, {
      ...removePersistentResponseStatus
    }];
  };

  /**
   * Operations that are executed before file is downloaded.
   */
  getBeforeDownloadOperations(): Operation[] {
    if (!this.withIndicator) return [];
    const closeWidget = this.getCloseWidgetOperation();

    let operations: Operation[] = [{
      actionName: this.actionName,
      condition: '{numberOfActiveDownloads()} === 1 || {closingWidget}', // == 1 in that moment only one dispdownloads file wich will be unlinked lather
      conditionOperations: [closeWidget, ...this.getUpdateActiveDownloadCount()],
      elseOperations: [closeWidget],
      ...this.getEditorAdditionalData(),
      type: OperationType.conditional
    }, ...this.beforeDownloadOperations];

    // <debug> - used only in combination with dispatcher editor
    operations = operations.map((o: any) => DispatcherManager.addComplexOperationParentId(o, this.editorData.complexOperationId));
    // </debug>
    return operations;
  }

  private getPingerOperation(dispatcherId: string): StackOperation {
    const { actionName, mappingName, delay } = this;
    return {
      actionName,
      data: {
        template: {
          params: {
            bind: {
              downloadId: `{${mappingName}} && {${mappingName}.downloadId}`
            }
          }
        }
      },
      actionType: {
        dispatcherId,
        delay,
        loop: this.getLoopConfiguration(),
        pos: Position.end
      },
      independent: true,
      name: actionName, // calling myself
      ...this.getEditorAdditionalData(),
      type: OperationType.stack
    };
  }

  /**
   * Gets loop configuration for stack action.
   */
  getLoopConfiguration(): LoopObjectType {
    const { actionName, mappingName, pingCondition, debug } = this;
    const beforeDownloadOperations = this.getBeforeDownloadOperations();
    const editorData: object = this.getEditorAdditionalData();
    return {
      condition: `!{${mappingName}.${pingCondition}}`,
      breakOperations: [{
        actionName,
        // <debug>
        debug,
        // </debug>
        condition: this.pingBreakCondition.map((conditionPart: string) => `[null, undefined].indexOf({${mappingName}.${conditionPart}}) !== -1`).join('||'),
        conditionOperations: [...beforeDownloadOperations, {
          data: {
            template: {
              bind: {
                downloadId: `{${mappingName}} && {${mappingName}.downloadId}`
              }
            }
          },
          type: OperationType.data
        }, {
          actionName,
          data: this.download,
          mappingName,
          ...editorData,
          type: OperationType.request
        }, ...this.afterDownloadOperations, {
          name: 'FadeOutDownloadWidget',
          type: OperationType.action
        }],
        elseOperations: [], // TODO raise error
        ...editorData,
        type: OperationType.conditional
      }, this.getStopAllStacksInCurrentDispatcher(), this.getUnlinkCurrentDispatcher(), ...this.getComplexOperationEnd()]
    };
  }

  /**
   * Creates ping request.
   */
  private getPingRequest(): RequestOperation {
    return {
      data: this.ping,
      handleError: true,
      mappingName: this.mappingName,
      ...this.getEditorAdditionalData(),
      type: OperationType.request,
      // <debug>
      debug: this.debug
      // </debug>
    };
  }

  /**
   * Gets ping error warning operation.
   */
  private getPingErrorWarning(): Operation[] {
    const { actionName } = this;
    return [{
      actionName,
      data: {
        handleErrorStatuses: this.handleErrorStatuses || []
      },
      type: OperationType.data
    }, {
      actionName,
      condition: '{responseStatus} && !({handleErrorStatuses}.find((item)=>item==={responseStatus}))',
      conditionOperations: [{
        actionName,
        items: [{
          operations: [this.getStopAllStacksInCurrentDispatcher(), this.getUnlinkCurrentDispatcher(true)],
          btnName: 'downloadFailed.close',
          btnConfig: {
            fillMode: 'flat',
            pos: Position.end
          },
          runningOperationsAfterClose: true
        }],
        placeholders: {
          template: {
            bind: {
              anyText: this.getPingBreakConditionErrorValue()
            }
          }
        },
        title: 'downloadFailed.title',
        text: 'downloadFailed.text',
        ...this.getEditorAdditionalData(),
        warningType: WarningType.warning,
        type: OperationType.warning
      }],
      type: OperationType.conditional
    }];
  }

  /**
   * Creates closing widget operation.
   */
  private getCloseWidgetOperation() {
    return {
      actionName: this.actionName,
      data: {
        moduleId: DOWNLOAD_INDICATOR_WIDGET
      },
      moduleName: 'DownloadWidget',
      name: 'close',
      ...this.getEditorAdditionalData()
    };
  }

  private getUpdateActiveDownloadCount(): Operation[] {
    const { actionName, pingCondition } = this;
    const editorData: object = this.getEditorAdditionalData();
    let updateActiveDownloadCount: Action = [];

    if (this.withIndicator) {
      updateActiveDownloadCount = [{
        actionName,
        data: {
          template: {
            [pingCondition]: false,
            bind: {
              activeDownloadCount: '{numberOfActiveDownloads()} - 1'
            }
          }
        },
        type: OperationType.data,
        ...editorData
      }, {
        actionName,
        condition: this.getPingBreakConditionErrorValue(),
        conditionOperations: [{
          actionName,
          data: {
            [pingCondition]: true
          },
          type: OperationType.data,
          ...editorData
        }],
        type: OperationType.conditional,
        ...editorData
      }];
    }
    return updateActiveDownloadCount;
  }

  /**
   * Gets condition value that will break pinging.
   */
  private getPingBreakConditionErrorValue(): string {
    return this.pingBreakCondition.map((conditionPart: string) => `{${this.mappingName}.${conditionPart}}`).join('||');
  }

  /**
   * Prepends pingCondition value as first argument to every operation in passed pingOperations property.
   * @param operation
   * @param pingCondition Argument that will be at first place.
   */
  prependAdditionalArgsToPingOperations = (operation: any, pingCondition: string) => {
    operation.args = [pingCondition, ...(operation.args ? operation.args : [])];
    return operation;
  };

  /**
   * Gets start of complex operation for ping download.
   * @param o PingDownload operation
   * @dispatcherEditor
   */
  public getComplexOperationStart(o: PingDownloadOperation): Operation[] {
    // eslint-disable-next-line array-bracket-newline
    const { original } = this.editorData;
    return [
      // <debug>
      {
        ...this.getComplexOperationStartCommonData(o),
        ping: original.ping,
        download: original.download,
        editorData: {
          ping: this.ping,
          download: this.download
        }
      } as any
      // eslint-disable-next-line array-element-newline
      // </debug>
    ];
  }

  /**
   * Creates stopAll operation that stops all stacks in current dispatcher.
   */
  private getStopAllStacksInCurrentDispatcher(): StopAllOperation {
    return {
      actionName: this.actionName,
      items: [Stop.stacks],
      dispatcherIds: [this.appDispatcherId],
      ...this.getEditorAdditionalData(),
      type: OperationType.stopAll
    };
  }

  /**
   * Get unlink operation for current dispatcher.
   * @param independent If `true` operation will be independent
   */
  private getUnlinkCurrentDispatcher(independent?: boolean) {
    let operation: UnlinkOperation = {
      actionName: this.actionName,
      dispatcherIds: [this.appDispatcherId],
      ...this.getEditorAdditionalData(),
      type: OperationType.unlink
    };
    if (independent) {
      operation.independent = independent;
    }
    return operation;
  }

  /**
   * GETTERS AND SETTERS BELLOW
   */
  static get pingInvocation(): { [key: string]: string } {
    return this._pingInvocation;
  }

  static set pingInvocation(value: { [key: string]: string }) {
    this._pingInvocation = value;
  }

  get asyncDispatcherId(): string {
    return this._asyncDispatcherId;
  }

  set asyncDispatcherId(value: string) {
    this._asyncDispatcherId = value;
  }

  get callingDispatcherId(): string {
    return this._callingDispatcherId;
  }

  set callingDispatcherId(value: string) {
    this._callingDispatcherId = value;
  }

  get pingOperations(): Operation[] {
    return this._pingOperations;
  }

  set pingOperations(value: Operation[]) {
    this._pingOperations = value;
  }

  get mappingName(): string {
    return this._mappingName;
  }

  set mappingName(value: string) {
    this._mappingName = value;
  }

  get withIndicator(): boolean {
    return this._withIndicator;
  }

  set withIndicator(value: boolean) {
    this._withIndicator = value;
  }

  get download(): any {
    return this._download;
  }

  set download(value: any) {
    this._download = value;
  }

  get pingErrorOperations(): Operation[] {
    return this._pingErrorOperations;
  }

  set pingErrorOperations(value: Operation[]) {
    this._pingErrorOperations = value;
  }

  get pingBreakCondition(): string[] {
    return this._pingBreakCondition;
  }

  set pingBreakCondition(value: string[]) {
    this._pingBreakCondition = value;
  }

  get pingCondition(): string {
    return this._pingCondition;
  }

  set pingCondition(value: string) {
    this._pingCondition = value;
  }

  get delay(): number {
    return this._delay;
  }

  set delay(value: number) {
    this._delay = value;
  }

  get ping(): any {
    return this._ping;
  }

  set ping(value: any) {
    this._ping = value;
  }

  get afterDownloadOperations(): Operation[] {
    return this._afterDownloadOperations;
  }

  set afterDownloadOperations(value: Operation[]) {
    this._afterDownloadOperations = value;
  }

  get beforeDownloadOperations(): Operation[] {
    return this._beforeDownloadOperations;
  }

  set beforeDownloadOperations(value: Operation[]) {
    this._beforeDownloadOperations = value;
  }
}

export default PingDownload;
